import React, { useState, useEffect, useRef } from 'react';
import { MdHelpOutline } from 'react-icons/md';
import { AiFillStar, AiOutlineStar } from 'react-icons/ai';
import Tree from 'rc-tree';
import 'rc-tree/assets/index.css';
import { RC_TREE_MOTION } from 'utils/rcTreeMotion';
import './style.scss';
import { FiRefreshCw } from 'react-icons/fi';
import { BsExclamationLg } from 'react-icons/bs';
import { Menu, Item, useContextMenu } from 'react-contexify';
import { MdColorLens, MdFullscreenExit } from 'react-icons/md';
import Symbology from './symbology';
import { SymbologyModelRecord } from 'models/SymbologyModel';
import { RgbaColor } from 'react-colorful';
import { useSurveyManagerActions } from 'actions/surveymanager';
import { loadSurveyManagerCatalogue } from './loadSurveyManagerCatalogue';
import { surveyImageViewerGuideUrl } from 'constants/guideRoutes';
import type { ContextMenuLayerNode } from 'types/layerRefresh';
import { SURVEY_WFS_LAYER_SOURCE } from 'types/layerRefresh';
import {
  FAVOURITES_CHANGED_EVENT,
  type FavouriteLayerNode,
  type FavouritesChangedDetail,
  dispatchFavouritesChanged,
  getFavouriteBaseKeys,
  loadFavouritesMap,
  saveFavouritesMap,
  toggleFavourite,
} from 'utils/favouritesStorage';
import { SURVEY_COVERAGE_FAVOURITE_TYPE } from 'utils/layerEndPointUrl';

type Key = React.Key;

interface Props {
  onNodeCheckedSurveyManager: (checkedNodes: any[], currentSelectedNode?: any) => void;
  onSurveyLayerTypesLoaded: (layerTypes: string[]) => void;
  setTreeDataSurveyManager: React.Dispatch<React.SetStateAction<any[]>>;
  treeDataSurveyManager: any[];
  layerRefreshLoading: Record<string, { isLoading: boolean; hasError: boolean; errorMessage?: string }>;
  setCheckedNodesState: (p: any[]) => void;
  checkedNodesState: any[];
  /** Lifted to ToolBar so ticks survive remount when the right toolbar collapses. */
  checkedKeys: Key[];
  setCheckedKeys: React.Dispatch<React.SetStateAction<Key[]>>;
  handleContextMenuRefreshClicked: (
    selectedKeys: Key[],
    contextNode?: ContextMenuLayerNode | null,
  ) => void;
  styles: SymbologyModelRecord;
  setLayerStyle: (key: string, color: RgbaColor, width: number, size: number, lineStyle: string, markerStyle: string, angle: number,) => void;
  bringLayerToTop: (layerKey: string) => void;
  zoomToSection: (key: string) => void;
}

const MENU_ID = 'surveymanager-menu';

const normalizeTreeKey = (key: React.Key): string =>
  String(key).replace('favourites_', '');

const SurveyManager: React.FC<Props> = ({
  onNodeCheckedSurveyManager,
  onSurveyLayerTypesLoaded,
  setTreeDataSurveyManager,
  treeDataSurveyManager,
  layerRefreshLoading,
  setCheckedNodesState,
  checkedNodesState,
  checkedKeys,
  setCheckedKeys,
  handleContextMenuRefreshClicked,
  styles = {},
  setLayerStyle,
  bringLayerToTop,
  zoomToSection,
}) => {
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [contextMenuNode, setContextMenuNode] =
    useState<ContextMenuLayerNode | null>(null);
  const [favouriteLayerKeys, setFavouriteLayerKeys] = useState<Set<string>>(
    () => getFavouriteBaseKeys(loadFavouritesMap()),
  );
  const skipNextFavouritesEventRef = useRef(false);

  useEffect(() => {
    const clearSelection = () => setSelectedKeys([]);
    window.addEventListener('legend:layer-selected', clearSelection);
    return () => window.removeEventListener('legend:layer-selected', clearSelection);
  }, []);

  useEffect(() => {
    const onFavouritesChanged = (event: Event) => {
      if (skipNextFavouritesEventRef.current) {
        skipNextFavouritesEventRef.current = false;
        return;
      }
      const detail = (event as CustomEvent<FavouritesChangedDetail>).detail;
      if (!detail) {
        return;
      }
      setFavouriteLayerKeys(getFavouriteBaseKeys(new Map(detail.entries)));
    };
    window.addEventListener(FAVOURITES_CHANGED_EVENT, onFavouritesChanged);
    return () =>
      window.removeEventListener(FAVOURITES_CHANGED_EVENT, onFavouritesChanged);
  }, []);
  const [inDragMode, setInDragMode] = useState(false);
  const onDragEnd = () => setInDragMode(false);
  const onDragStart = () => setInDragMode(true);
  const { show } = useContextMenu({ id: MENU_ID });
  const [symbologyData, setSymbologyData] = useState({ key: '', category: '', title: '', });
  const [symbologyOpened, setSymbologyOpened] = useState(false);
  const surveyManagerActions = useSurveyManagerActions();
  const [loading, setLoading] = useState(() => treeDataSurveyManager.length === 0);

  useEffect(() => {
    const checkedKey = treeDataSurveyManager.find((data) => data.isChecked === true);
    if (checkedKey) setCheckedNodesState([checkedKey]);
  }, [treeDataSurveyManager]);

  useEffect(() => {
    const onGetSurveyManager = async () => {
      try {
        await loadSurveyManagerCatalogue(
          {
            getLayers: surveyManagerActions.getLayers,
            getSurveyShapefiles: surveyManagerActions.getSurveyShapefiles,
          },
          {
            onLayerTypesLoaded: (layerTypes) => {
              onSurveyLayerTypesLoaded([...layerTypes]);
            },
            onTreeUpdated: (tree) => {
              setTreeDataSurveyManager(tree);
              setLoading(false);
            },
          },
        );
      } catch (err) {
        console.error('Error fetching survey manager:', err);
        setLoading(false);
      }
    };

    if (treeDataSurveyManager.length > 0) {
      setLoading(false);
      return;
    }

    onGetSurveyManager();
  }, []);

  useEffect(() => {
    if (treeDataSurveyManager.length > 0) {
      setLoading(false);
    }
  }, [treeDataSurveyManager.length]);

  const validateKeys = (keys: Key[], treeData: any[]): Key[] => {
    const validKeys = new Set();
    const traverseNodes = (nodes: any[]) => {
      nodes.forEach((node) => {
        validKeys.add(node.key);
        if (node.children) traverseNodes(node.children);
      });
    };
    traverseNodes(treeData);
    return keys.filter((key) => validKeys.has(key));
  };

  const markLeafNodes = (node: any): any => ({
    ...node,
    checkable: !!node.isLeaf && !node.endpointPending && !!node.endPoint,
    selectable: !!node.isLeaf && !node.endpointPending && !!node.endPoint,
    children: node.children?.map(markLeafNodes),
  });

  const getNodes = (node: any): any[] => {
    if (node?.children?.length > 0 && node.children[0].isLeaf)
      return node.children;
    if (node?.children?.length > 0)
      return node.children.map((x: any) => getNodes(x)).flat();
    return [];
  };

  const getParentKeys = (key: Key, nodes: any[], parentKeys: Key[] = [],): Key[] => {
    for (const node of nodes) {
      if (node.children) {
        for (const child of node.children) {
          if (child.key === key) return [...parentKeys, node.key];
          const found = getParentKeys(key, node.children, [
            ...parentKeys,
            node.key,
          ]);
          if (found.length) return found;
        }
      }
    }
    return [];
  };

  const getAllParentKeys = (checkedKeys: Key[], treeData: any[]): Key[] => {
    const parentKeys = new Set<Key>();
    checkedKeys.forEach((key) => {
      const parents = getParentKeys(key, treeData);
      parents.forEach((parentKey) => parentKeys.add(parentKey));
    });
    return Array.from(parentKeys);
  };

  const getParentTitleByNodeKey = (
    nodeKey: Key,
    nodes: any[],
    parentTitle?: string,
  ): string | undefined => {
    const normalizedNodeKey = normalizeTreeKey(nodeKey);

    for (const node of nodes) {
      if (normalizeTreeKey(node.key) === normalizedNodeKey) {
        return parentTitle;
      }

      if (node.children?.length) {
        const foundParentTitle = getParentTitleByNodeKey(
          nodeKey,
          node.children,
          node.title,
        );
        if (foundParentTitle) return foundParentTitle;
      }
    }

    return undefined;
  };

  const nodeChecked = (checked: Key[] | { checked: Key[]; halfChecked: Key[] }, info: { event: 'check'; node: any; checked: boolean; halfCheckedKeys?: Key[]; },) => {
    if (info.node.isLeaf && info.checked && (info.node.endpointPending || !info.node.endPoint)) {
      return;
    }
    const uniqueCombined = [
      ...new Set([
        ...(Array.isArray(checked) ? checked : checked.checked)
      ]),
    ];
    let localChecked = [...uniqueCombined];
    setCheckedKeys(localChecked);
    if (info.node.isLeaf) {
      if (!info.checked && info.node.key === (selectedKeys.length > 0 ? selectedKeys[0] : null)) {
        setSelectedKeys([]);
      }
      onNodeCheckedSurveyManager([{
        checked: info.checked,
        title: info.node.title,
        key: info.node.key.replace('favourites_', ''),
        endPoint: info.node.endPoint,
        category: info.halfCheckedKeys?.[0],
        layerSource: info.node.layerSource ?? SURVEY_WFS_LAYER_SOURCE,
      },]);
    }
  };

  const nodeSelected = (
    selectedKeys: Key[],
    info: {
      event: 'select';
      selected: boolean;
      node: any;
      selectedNodes: any[];
      nativeEvent: MouseEvent;
    },
  ) => {
    if (info.selected) {
      bringLayerToTop(info.node.key);
      setSelectedKeys([info.node.key]);
      window.dispatchEvent(new CustomEvent('surveymanager:layer-selected'));

      if (!info.node.checked) {
        onNodeCheckedSurveyManager(
          [
            {
              checked: true,
              title: info.node.title,
              key: info.node.key.replace('favourites_', ''),
              endPoint: info.node.endPoint,
              category: info.node.category,
            },
          ],
          {
            key: info.node.key.replace('favourites_', ''),
            title: info.node.title,
            category: info.node.category,
          },
        );
        setCheckedKeys((x) => [
          ...x,
          info.node.key,
          info.node.key.startsWith('favourites_')
            ? info.node.key.replace('favourites_', '')
            : `favourites_${info.node.key}`,
        ]);
      } else {
        onNodeCheckedSurveyManager([], {
          key: info.node.key.replace('favourites_', ''),
          title: info.node.title,
          category: info.node.category,
        });
      }
    } else {
      setSelectedKeys([]);
    }
  };

  const onRightClick = (info: { event: React.MouseEvent; node: any }) => {
    if (info.node.isLeaf && !info.node.checked) {
      setCheckedKeys((ch) => [
        ...new Set([
          ...ch,
          info.node.key,
          info.node.key.startsWith('favourites_')
            ? info.node.key.replace('favourites_', '')
            : `favourites_${info.node.key}`,
        ]),
      ]);
      onNodeCheckedSurveyManager([
        {
          checked: true,
          title: info.node.title,
          key: info.node.key.replace('favourites_', ''),
          endPoint: info.node.endPoint,
          category: info.node.category,
          layerSource: info.node.layerSource ?? SURVEY_WFS_LAYER_SOURCE,
        },
      ]);
      bringLayerToTop(info.node.key.replace('favourites_', ''));
      setSelectedKeys([info.node.key.replace('favourites_', '')]);
      window.dispatchEvent(new CustomEvent('surveymanager:layer-selected'));
    }

    if (info.node.isLeaf && info.node.checked) {
      setContextMenuNode({
        key: info.node.key.replace('favourites_', ''),
        endPoint: info.node.endPoint,
        title: info.node.title,
        category: info.node.category,
        layerSource: info.node.layerSource ?? SURVEY_WFS_LAYER_SOURCE,
      });

      const parentTitle = getParentTitleByNodeKey(
        info.node.key,
        treeDataSurveyManager,
      );

      show({
        event: info.event,
        props: {
          key: info.node.key.replace('favourites_', ''),
          category: info.node.category,
          title: info.node.title,
          parentTitle,
          parameterId: info.node.parameterId,
        },
      });
      bringLayerToTop(info.node.key.replace('favourites_', ''));
      setSelectedKeys([info.node.key.replace('favourites_', '')]);
      window.dispatchEvent(new CustomEvent('surveymanager:layer-selected'));
    }
  };

  const handleOpenSymbology = (ev: any) => {
    const titleWithParent = ev.props.parentTitle
      ? `${ev.props.parentTitle} - ${ev.props.title}`
      : ev.props.title;

    setSymbologyData({
      key: ev.props.key,
      category: ev.props.category,
      title: titleWithParent,
    });
    setSymbologyOpened(true);
  };

  const handleZoomToSection = (ev: any) => zoomToSection(ev.props.key);

  const findParentNode = (
    key: string,
    nodes: readonly { readonly key?: React.Key; readonly title?: React.ReactNode; readonly children?: readonly unknown[] }[],
  ): { readonly title?: React.ReactNode } | null => {
    for (const node of nodes) {
      if (node.children?.some((child) => {
        const childNode = child as { readonly key?: React.Key };
        return String(childNode.key) === key;
      })) {
        return node;
      }
      if (node.children) {
        const found = findParentNode(
          key,
          node.children as readonly { readonly key?: React.Key; readonly title?: React.ReactNode; readonly children?: readonly unknown[] }[],
        );
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  const handleFavouriteClick = (
    nodeData: {
      readonly key: string;
      readonly title?: React.ReactNode;
      readonly endPoint?: string;
      readonly endpoint?: string;
      readonly category?: string;
      readonly type?: string;
    },
    event: React.MouseEvent,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const parentNode = findParentNode(nodeData.key, treeDataSurveyManager);
    const parentName = parentNode?.title != null
      ? String(parentNode.title)
      : nodeData.category || 'Survey Image Viewer';
    const favouriteNode: FavouriteLayerNode = {
      key: normalizeTreeKey(nodeData.key),
      title: String(nodeData.title ?? nodeData.key),
      endPoint: nodeData.endPoint ?? nodeData.endpoint,
      endpoint: nodeData.endpoint ?? nodeData.endPoint,
      type: SURVEY_COVERAGE_FAVOURITE_TYPE,
      category: nodeData.category,
      layerSource: SURVEY_WFS_LAYER_SOURCE,
      isLeaf: true,
    };
    const current = loadFavouritesMap();
    const { favourites: nextFavourites, isAdding } = toggleFavourite(
      current,
      favouriteNode,
      parentName,
    );
    saveFavouritesMap(nextFavourites);
    setFavouriteLayerKeys(getFavouriteBaseKeys(nextFavourites));
    skipNextFavouritesEventRef.current = true;
    dispatchFavouritesChanged(nextFavourites, isAdding);
  };

  const titleRender = (nodeData: any) => {
    let isSelectedKeysRefreshing: {
      [key: string]: {
        [x: string]: any;
        isLoading: boolean;
        hasError: boolean;
      };
    } = {};
    if (layerRefreshLoading.hasOwnProperty(nodeData.key)) {
      isSelectedKeysRefreshing[nodeData.key] =
        layerRefreshLoading[nodeData.key];
    }

    const isChecked = checkedKeys.includes(nodeData.key);
    const parentKeys = getAllParentKeys(checkedKeys, treeDataSurveyManager);
    const isParentChecked = parentKeys.includes(nodeData.key);
    const isSelected =
      (isChecked || isParentChecked);
    const isParentNode = !nodeData.isLeaf;

    return (
      <div className="title_render--container">
        <div
          className={`title_render--container_title ${isSelected ? 'selected-title' : ''}${isParentNode ? ' title_render--container_title--expandable' : ''}`}
          title={
            isParentNode
              ? expandedKeys.includes(nodeData.key)
                ? 'Click to collapse'
                : 'Click to expand'
              : `${nodeData.title}`
          }
          onClick={
            isParentNode
              ? (event: React.MouseEvent) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setExpandedKeys((prev) =>
                    prev.includes(nodeData.key)
                      ? prev.filter((key) => key !== nodeData.key)
                      : [...prev, nodeData.key],
                  );
                }
              : undefined
          }
        >
          {nodeData.title}
        </div>
        {nodeData.isLeaf && (
          <div className="title_render--status">
            {(nodeData.endpointPending ||
              isSelectedKeysRefreshing?.[nodeData.key]?.isLoading) && (
              <FiRefreshCw className="rotating" aria-label="Loading layer" />
            )}
            {!nodeData.endpointPending &&
              !nodeData.endPoint &&
              !isSelectedKeysRefreshing?.[nodeData.key]?.isLoading && (
                <span
                  title="No coverage data available"
                  style={{ display: 'inline-flex', alignItems: 'center' }}
                >
                  <BsExclamationLg color="red" size={20} aria-hidden />
                </span>
              )}
            {(isSelectedKeysRefreshing?.[nodeData.key]?.hasError) && (
              <span
                title={
                  isSelectedKeysRefreshing?.[nodeData.key]?.errorMessage ??
                  'Survey layer error'
                }
                style={{ display: 'inline-flex', alignItems: 'center' }}
              >
                <BsExclamationLg color="red" size={20} />
              </span>
            )}
            {favouriteLayerKeys.has(normalizeTreeKey(String(nodeData.key))) ? (
              <AiFillStar
                className="favourites_star_img"
                color="#FFC845"
                size={19}
                onClick={(event) => handleFavouriteClick(nodeData, event)}
              />
            ) : (
              <AiOutlineStar
                className="favourites_star_img"
                size={19}
                onClick={(event) => handleFavouriteClick(nodeData, event)}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {symbologyOpened && (
        <Symbology
          isOpen={symbologyOpened}
          closeModal={() => setSymbologyOpened(false)}
          itemKey={symbologyData.key}
          category={symbologyData.category}
          title={symbologyData.title}
          setLayerStyle={setLayerStyle}
          currentWidth={styles[symbologyData.key]?.currentWidth}
          currentSize={styles[symbologyData.key]?.currentSize}
          currentLineStyle={styles[symbologyData.key]?.currentLineStyle}
          currentMarkerStyle={styles[symbologyData.key]?.currentMarkerStyle}
          currentColor={styles[symbologyData.key]?.currentColor}
          currentAngle={styles[symbologyData.key]?.currentAngle}
          layerType={styles[symbologyData.key]?.type}
        />
      )}
      {!inDragMode && (
        <Menu id={MENU_ID}>
          <Item onClick={handleOpenSymbology}>
            <MdColorLens />
            Symbology
          </Item>
          <Item onClick={handleZoomToSection}>
            <MdFullscreenExit />
            Zoom to extent
          </Item>
          <Item
            onClick={() => {
              handleContextMenuRefreshClicked(selectedKeys, contextMenuNode);
            }}
          >
            <FiRefreshCw size={10} style={{ marginRight: '5px' }} />
            Refresh
          </Item>
        </Menu>
      )}
      <div className="xa-surveymanager-container">
        <div className="xa-surveymanager-header">
          <a
            href={surveyImageViewerGuideUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="xa-surveymanager-guide-link"
          >
            <MdHelpOutline aria-hidden="true" />
            <span>How to use this tool</span>
          </a>
        </div>
        <div className="xa-surveymanager-body">
          {loading ? (
            <div className="xa-surveymanager-empty-state" role="status" aria-live="polite">
              <FiRefreshCw className="rotating" aria-hidden />
              <span>Loading layers…</span>
            </div>
          ) : treeDataSurveyManager.length === 0 ? (
            <div className="xa-surveymanager-empty-state" role="status">
              <BsExclamationLg color="red" size={20} aria-hidden />
              <span>No data available</span>
            </div>
          ) : (
            <Tree
              showLine
              showIcon={false}
              checkable
              selectable
              expandAction="click"
              motion={RC_TREE_MOTION}
              treeData={treeDataSurveyManager.map(markLeafNodes)}
              checkedKeys={validateKeys(checkedKeys, treeDataSurveyManager)}
              selectedKeys={validateKeys(selectedKeys, treeDataSurveyManager)}
              expandedKeys={expandedKeys}
              onExpand={(keys) => setExpandedKeys(keys as React.Key[])}
              onCheck={nodeChecked}
              onSelect={nodeSelected}
              titleRender={titleRender}
              onRightClick={onRightClick}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default SurveyManager;
